<template>
  <div class="widget-carousel-product-editor">
    <div class="card border-0 shadow-sm compact-form-card">
      <div class="card-header bg-white border-bottom compact-form-card__header">
        <div class="d-flex flex-column">
          <strong>
            {{ $t("superUpLang.widget.carousel.productEditor.title") }}:
            {{ product.product_name || $t('superUpLang.widget.carousel.productEditor.unnamedProduct') }}
          </strong>

          <div
              v-if="!product.is_active"
              class="widget-carousel-product-editor__inactive-box"
          >
            <span class="badge badge-danger">{{ $t("superUpLang.widget.carousel.common.inactive") }}</span>
            <div
                v-if="product.reason"
                class="widget-carousel-product-editor__inactive-reason"
            >
              {{ product.reason }}
            </div>
          </div>
        </div>
      </div>

      <div class="card-body compact-form-card__body">
        <div class="widget-carousel-product-editor__section">
          <h5 class="widget-carousel-product-editor__section-title">
            {{ $t("superUpLang.widget.carousel.productEditor.sections.productStatus") }}
          </h5>

          <div class="form-group form-check compact-check-group">
            <input
                :id="`product-is-active-${product.local_id}`"
                type="checkbox"
                class="form-check-input"
                :checked="product.is_active"
                :disabled="true"
                @change="updateProductField('is_active', $event.target.checked)"
            >
            <label
                class="form-check-label compact-check-label"
                :for="`product-is-active-${product.local_id}`"
            >
              {{ $t("superUpLang.widget.carousel.productEditor.fields.productActive") }}
            </label>
          </div>

          <div class="form-group compact-form-group mb-0">
            <label class="compact-label">{{ $t("superUpLang.widget.carousel.productEditor.fields.reason") }}</label>
            <input
                type="text"
                class="form-control compact-input"
                :value="product.reason"
                :disabled="true"
                @input="updateProductField('reason', $event.target.value)"
                :placeholder="$t('superUpLang.widget.carousel.productEditor.placeholders.reason')"
            >
          </div>
        </div>

        <div
            v-if="shouldShowProductImageInput"
            class="widget-carousel-product-editor__section"
        >
          <h5 class="widget-carousel-product-editor__section-title">
            {{ $t("superUpLang.widget.carousel.productEditor.sections.productImage") }}
          </h5>

          <div class="form-group compact-form-group">
            <label class="compact-label" :for="`product-image-${product.local_id}`">
              {{ $t("superUpLang.widget.carousel.productEditor.fields.uploadImage") }}
            </label>

            <input
                :id="`product-image-${product.local_id}`"
                :ref="`productImageInput_${product.local_id}`"
                type="file"
                class="form-control-file compact-file-input"
                accept="image/*"
                :disabled="isDisabled"
                @change="onProductImageChange"
            >

            <small class="compact-help-text d-block mt-1">
              {{ $t("superUpLang.widget.carousel.productEditor.help.productImage") }}
            </small>
          </div>

          <div v-if="product.image.error" class="widget-carousel-product-editor__error">
            {{ product.image.error }}
          </div>

          <div v-if="product.image.preview" class="widget-carousel-product-editor__image-preview-wrap">
            <img
                :src="product.image.preview"
                :alt="$t('superUpLang.widget.carousel.productEditor.alt.productImagePreview')"
                class="widget-carousel-product-editor__image-preview"
            >

            <button
                type="button"
                class="btn btn-outline-danger btn-sm mt-2"
                :disabled="isDisabled"
                @click="removeProductImage"
            >
              {{ $t("superUpLang.widget.carousel.productEditor.actions.removeImage") }}
            </button>
          </div>
        </div>

        <div class="widget-carousel-product-editor__section">
          <h5 class="widget-carousel-product-editor__section-title">
            {{ $t("superUpLang.widget.carousel.productEditor.sections.voucherLink") }}
          </h5>

          <div class="form-group form-check compact-check-group">
            <input
                :id="`voucher-url-default-${product.local_id}`"
                type="checkbox"
                class="form-check-input"
                :checked="isVoucherUrlDefault"
                :disabled="isDisabled"
                @change="onVoucherUrlDefaultChange($event.target.checked)"
            >
            <label
                class="form-check-label compact-check-label"
                :for="`voucher-url-default-${product.local_id}`"
            >
              {{ $t("superUpLang.widget.carousel.productEditor.fields.useAutomaticUrl") }}
            </label>
          </div>

          <div class="form-group compact-form-group mb-0">
            <label class="compact-label" :for="`voucher-url-${product.local_id}`">
              {{ $t("superUpLang.widget.carousel.productEditor.fields.voucherUrl") }}
            </label>

            <input
                :id="`voucher-url-${product.local_id}`"
                type="text"
                class="form-control compact-input"
                :value="voucherUrlInputValue"
                :disabled="isDisabled || isVoucherUrlDefault"
                :required="!isVoucherUrlDefault"
                @input="onVoucherUrlInput"
                :placeholder="$t('superUpLang.widget.carousel.productEditor.placeholders.voucherUrl')"
            >

            <small class="compact-help-text d-block mt-1">
              {{ $t("superUpLang.widget.carousel.productEditor.help.voucherUrl") }}
            </small>
          </div>
        </div>

        <div class="widget-carousel-product-editor__section mb-0">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <h5 class="widget-carousel-product-editor__section-title mb-0">
              {{ $t("superUpLang.widget.carousel.productEditor.sections.badges") }}
            </h5>
          </div>

          <div class="widget-carousel-product-editor__badge-grid">
            <div
                v-for="position in badgePositions"
                :key="position.key"
                class="widget-carousel-product-editor__badge-slot"
            >
              <div class="widget-carousel-product-editor__badge-slot-header">
                <strong>{{ position.label }}</strong>

                <button
                    type="button"
                    class="btn btn-outline-primary btn-sm"
                    :disabled="isDisabled"
                    @click="addBadge(position.key)"
                >
                  {{ $t("superUpLang.widget.carousel.productEditor.actions.addBadge") }}
                </button>
              </div>

              <div v-if="getBadgeList(position.key).length === 0" class="widget-carousel-product-editor__badge-empty">
                {{ $t("superUpLang.widget.carousel.productEditor.emptyBadges") }}
              </div>

              <div
                  v-for="(badge, badgeIndex) in getBadgeList(position.key)"
                  :key="badge.local_id"
                  class="widget-carousel-product-editor__badge-item"
              >
                <div class="widget-carousel-product-editor__badge-item-header">
                  <strong>{{ $t("superUpLang.widget.carousel.productEditor.badgeNumber", { index: badgeIndex + 1 }) }}</strong>

                  <button
                      type="button"
                      class="btn btn-outline-danger btn-sm"
                      :disabled="isDisabled"
                      @click="removeBadge(position.key, badgeIndex)"
                  >
                    {{ $t("superUpLang.widget.carousel.productsList.actions.delete") }}
                  </button>
                </div>

                <div class="form-row compact-form-row">
                  <div class="form-group col-12 col-md-6 compact-form-group">
                    <label class="compact-label">{{ $t("superUpLang.widget.carousel.productEditor.fields.contentMode") }}</label>
                    <select
                        class="form-control compact-input"
                        :value="badge.content_mode"
                        :disabled="isDisabled"
                        @change="updateBadgeField(position.key, badgeIndex, 'content_mode', $event.target.value)"
                    >
                      <option value="custom">{{ $t("superUpLang.widget.carousel.productEditor.contentModes.custom") }}</option>
                      <option value="product_variable">{{ $t("superUpLang.widget.carousel.productEditor.contentModes.productVariable") }}</option>
                      <option value="mixed">{{ $t("superUpLang.widget.carousel.productEditor.contentModes.mixed") }}</option>
                    </select>
                  </div>
                </div>

                <div v-if="badge.content_mode === 'mixed'" class="form-group compact-form-group">
                  <label class="compact-label d-block">{{ $t("superUpLang.widget.carousel.productEditor.fields.insertVariables") }}</label>
                  <div class="widget-carousel-product-editor__token-list">
                    <button
                        v-for="variable in productVariables"
                        :key="variable.key"
                        type="button"
                        class="btn btn-outline-secondary btn-sm"
                        :disabled="isDisabled"
                        @click="appendToken(position.key, badgeIndex, variable.key)"
                    >
                      {{ buildToken(variable.key) }}
                    </button>
                  </div>
                </div>

                <div
                    v-if="badge.content_mode === 'custom' || badge.content_mode === 'mixed'"
                    class="form-row compact-form-row"
                >
                  <div class="form-group col-12 col-md-6 compact-form-group">
                    <label class="compact-label">{{ $t("superUpLang.widget.carousel.productEditor.fields.titleRo") }}</label>
                    <input
                        type="text"
                        class="form-control compact-input"
                        :value="badge.title.ro"
                        :disabled="isDisabled"
                        @input="updateBadgeI18n(position.key, badgeIndex, 'title', 'ro', $event.target.value)"
                    >
                  </div>

                  <div class="form-group col-12 col-md-6 compact-form-group">
                    <label class="compact-label">{{ $t("superUpLang.widget.carousel.productEditor.fields.titleEn") }}</label>
                    <input
                        type="text"
                        class="form-control compact-input"
                        :value="badge.title.en"
                        :disabled="isDisabled"
                        @input="updateBadgeI18n(position.key, badgeIndex, 'title', 'en', $event.target.value)"
                    >
                  </div>
                </div>

                <div class="form-row compact-form-row">
                  <div class="form-group col-12 col-md-6 compact-form-group">
                    <label class="compact-label">{{ $t("superUpLang.widget.carousel.productEditor.fields.titleColor") }}</label>
                    <div class="widget-carousel-product-editor__color-row">
                      <input
                          type="color"
                          class="form-control widget-carousel-product-editor__color-input"
                          :value="normalizeColor(badge.title_color)"
                          :disabled="isDisabled"
                          @input="updateBadgeField(position.key, badgeIndex, 'title_color', $event.target.value)"
                      >
                      <input
                          type="text"
                          class="form-control compact-input"
                          :value="badge.title_color"
                          :disabled="isDisabled"
                          @input="updateBadgeField(position.key, badgeIndex, 'title_color', $event.target.value)"
                      >
                    </div>
                  </div>

                  <div class="form-group col-12 col-md-6 compact-form-group">
                    <label class="compact-label d-block">{{ $t("superUpLang.widget.carousel.productEditor.fields.transparentBackground") }}</label>
                    <div class="form-check compact-check-group mt-2">
                      <input
                          :id="`badge-transparent-${product.local_id}-${position.key}-${badgeIndex}`"
                          type="checkbox"
                          class="form-check-input"
                          :checked="isTransparentBackground(badge.background_color)"
                          :disabled="isDisabled"
                          @change="setBadgeTransparent(position.key, badgeIndex, $event.target.checked)"
                      >
                      <label
                          class="form-check-label compact-check-label"
                          :for="`badge-transparent-${product.local_id}-${position.key}-${badgeIndex}`"
                      >
                        {{ $t("superUpLang.widget.carousel.common.transparent") }}
                      </label>
                    </div>
                  </div>
                </div>

                <div class="form-group compact-form-group">
                  <label class="compact-label">{{ $t("superUpLang.widget.carousel.productEditor.fields.backgroundColor") }}</label>
                  <div class="widget-carousel-product-editor__color-row">
                    <input
                        type="color"
                        class="form-control widget-carousel-product-editor__color-input"
                        :value="normalizeColor(badge.background_color)"
                        :disabled="isDisabled || isTransparentBackground(badge.background_color)"
                        @input="updateBadgeField(position.key, badgeIndex, 'background_color', $event.target.value)"
                    >
                    <input
                        type="text"
                        class="form-control compact-input"
                        :value="badge.background_color"
                        :disabled="isDisabled || isTransparentBackground(badge.background_color)"
                        @input="updateBadgeField(position.key, badgeIndex, 'background_color', $event.target.value)"
                        :placeholder="$t('superUpLang.widget.carousel.productEditor.placeholders.hexColor')"
                    >
                  </div>

                  <small
                      v-if="isTransparentBackground(badge.background_color)"
                      class="compact-help-text d-block mt-1"
                  >
                    {{ $t("superUpLang.widget.carousel.productEditor.help.uncheckTransparent") }}
                  </small>
                </div>

                <div class="form-group compact-form-group">
                  <label class="compact-label">{{ $t("superUpLang.widget.carousel.productEditor.fields.badgeImage") }}</label>

                  <input
                      :id="`badge-image-${product.local_id}-${position.key}-${badgeIndex}`"
                      type="file"
                      class="form-control-file compact-file-input"
                      accept="image/*"
                      :disabled="isDisabled"
                      @change="onBadgeImageChange(position.key, badgeIndex, $event)"
                  >

                  <div
                      v-if="badge.image_preview"
                      class="widget-carousel-product-editor__image-preview-wrap"
                  >
                    <img
                        :src="badge.image_preview"
                        :alt="$t('superUpLang.widget.carousel.productEditor.alt.badgePreview')"
                        class="widget-carousel-product-editor__image-preview"
                    >

                    <button
                        type="button"
                        class="btn btn-outline-danger btn-sm mt-2"
                        :disabled="isDisabled"
                        @click="removeBadgeImage(position.key, badgeIndex)"
                    >
                      {{ $t("superUpLang.widget.carousel.productEditor.actions.removeImage") }}
                    </button>
                  </div>
                </div>

                <div class="form-row compact-form-row">
                  <div class="form-group col-6 col-md-3 compact-form-group">
                    <label class="compact-label">{{ $t("superUpLang.widget.carousel.productEditor.fields.cornerRadius") }}</label>
                    <input
                        type="number"
                        class="form-control compact-input"
                        :value="badge.corner_radius"
                        :disabled="isDisabled"
                        @input="updateBadgeNumber(position.key, badgeIndex, 'corner_radius', $event.target.value)"
                    >
                  </div>

                  <div class="form-group col-6 col-md-3 compact-form-group">
                    <label class="compact-label d-block">{{ $t("superUpLang.widget.carousel.productEditor.fields.useDShape") }}</label>
                    <div class="form-check compact-check-group mt-2">
                      <input
                          :id="`badge-d-shape-${product.local_id}-${position.key}-${badgeIndex}`"
                          type="checkbox"
                          class="form-check-input"
                          :checked="badge.use_d_shape"
                          :disabled="isDisabled"
                          @change="updateBadgeField(position.key, badgeIndex, 'use_d_shape', $event.target.checked)"
                      >
                      <label
                          class="form-check-label compact-check-label"
                          :for="`badge-d-shape-${product.local_id}-${position.key}-${badgeIndex}`"
                      >
                        {{ $t("superUpLang.widget.carousel.common.active") }}
                      </label>
                    </div>
                  </div>
                </div>

                <div class="form-row compact-form-row">
                  <div class="form-group col-6 col-md-3 compact-form-group">
                    <label class="compact-label">{{ $t("superUpLang.widget.carousel.productEditor.fields.paddingTop") }}</label>
                    <input
                        type="number"
                        class="form-control compact-input"
                        :value="badge.paddings.top"
                        :disabled="isDisabled"
                        @input="updateBadgePadding(position.key, badgeIndex, 'top', $event.target.value)"
                    >
                  </div>

                  <div class="form-group col-6 col-md-3 compact-form-group">
                    <label class="compact-label">{{ $t("superUpLang.widget.carousel.productEditor.fields.paddingLeft") }}</label>
                    <input
                        type="number"
                        class="form-control compact-input"
                        :value="badge.paddings.left"
                        :disabled="isDisabled"
                        @input="updateBadgePadding(position.key, badgeIndex, 'left', $event.target.value)"
                    >
                  </div>

                  <div class="form-group col-6 col-md-3 compact-form-group">
                    <label class="compact-label">{{ $t("superUpLang.widget.carousel.productEditor.fields.paddingRight") }}</label>
                    <input
                        type="number"
                        class="form-control compact-input"
                        :value="badge.paddings.right"
                        :disabled="isDisabled"
                        @input="updateBadgePadding(position.key, badgeIndex, 'right', $event.target.value)"
                    >
                  </div>

                  <div class="form-group col-6 col-md-3 compact-form-group">
                    <label class="compact-label">{{ $t("superUpLang.widget.carousel.productEditor.fields.paddingBottom") }}</label>
                    <input
                        type="number"
                        class="form-control compact-input"
                        :value="badge.paddings.bottom"
                        :disabled="isDisabled"
                        @input="updateBadgePadding(position.key, badgeIndex, 'bottom', $event.target.value)"
                    >
                  </div>
                </div>

                <div class="form-row compact-form-row">
                  <div class="form-group col-6 col-md-3 compact-form-group">
                    <label class="compact-label">{{ $t("superUpLang.widget.carousel.productEditor.fields.marginTop") }}</label>
                    <input
                        type="number"
                        class="form-control compact-input"
                        :value="badge.margins.top"
                        :disabled="isDisabled"
                        @input="updateBadgeMargin(position.key, badgeIndex, 'top', $event.target.value)"
                    >
                  </div>

                  <div class="form-group col-6 col-md-3 compact-form-group">
                    <label class="compact-label">{{ $t("superUpLang.widget.carousel.productEditor.fields.marginLeft") }}</label>
                    <input
                        type="number"
                        class="form-control compact-input"
                        :value="badge.margins.left"
                        :disabled="isDisabled"
                        @input="updateBadgeMargin(position.key, badgeIndex, 'left', $event.target.value)"
                    >
                  </div>

                  <div class="form-group col-6 col-md-3 compact-form-group">
                    <label class="compact-label">{{ $t("superUpLang.widget.carousel.productEditor.fields.marginRight") }}</label>
                    <input
                        type="number"
                        class="form-control compact-input"
                        :value="badge.margins.right"
                        :disabled="isDisabled"
                        @input="updateBadgeMargin(position.key, badgeIndex, 'right', $event.target.value)"
                    >
                  </div>

                  <div class="form-group col-6 col-md-3 compact-form-group">
                    <label class="compact-label">{{ $t("superUpLang.widget.carousel.productEditor.fields.marginBottom") }}</label>
                    <input
                        type="number"
                        class="form-control compact-input"
                        :value="badge.margins.bottom"
                        :disabled="isDisabled"
                        @input="updateBadgeMargin(position.key, badgeIndex, 'bottom', $event.target.value)"
                    >
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
              v-if="isDisabled && !product.is_active"
              class="widget-carousel-product-editor__locked-note"
          >
            {{ $t("superUpLang.widget.carousel.productEditor.inactiveWarning") }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const STORE_NAMESPACE = 'widgetCarousel';
const MAX_IMAGE_SIZE_BYTES = 1024 * 1024;

export default {
  name: 'WidgetCarouselProductEditor',

  props: {
    product: {
      type: Object,
      required: true
    },
    isDisabled: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      badgePositions: [
        { key: 'top_left', label: this.$t('superUpLang.widget.carousel.common.positions.topLeft') },
        { key: 'top_right', label: this.$t('superUpLang.widget.carousel.common.positions.topRight') },
        { key: 'bottom_left', label: this.$t('superUpLang.widget.carousel.common.positions.bottomLeft') },
        { key: 'bottom_right', label: this.$t('superUpLang.widget.carousel.common.positions.bottomRight') }
      ]
    };
  },

  computed: {
    isOffersMode() {
      return this.product && this.product.source_type === 'offers';
    },

    productVariables() {
      if (this.isOffersMode) {
        return [
          { key: 'name', label: this.$t('superUpLang.widget.carousel.productEditor.productVariables.name') },
          { key: 'brand_name', label: this.$t('superUpLang.widget.carousel.productEditor.productVariables.brandName') },
          { key: 'value', label: this.$t('superUpLang.widget.carousel.productEditor.productVariables.value') }
        ];
      }

      return [
        { key: 'product_name', label: this.$t('superUpLang.widget.carousel.productEditor.productVariables.productName') },
        { key: 'price', label: this.$t('superUpLang.widget.carousel.productEditor.productVariables.price') },
        { key: 'discount_price', label: this.$t('superUpLang.widget.carousel.productEditor.productVariables.reducedPrice') },
        { key: 'user_discount', label: this.$t('superUpLang.widget.carousel.productEditor.productVariables.reducedUserDiscount') },
        { key: 'brand_name', label: this.$t('superUpLang.widget.carousel.productEditor.productVariables.brandName') },
        { key: 'discount', label: this.$t('superUpLang.widget.carousel.productEditor.productVariables.discount') },
      ];
    },

    shouldShowProductImageInput() {
      return !this.hasGlobalBackground;
    },

    hasGlobalBackground() {
      const store = this.$store && this.$store.state && this.$store.state[STORE_NAMESPACE];
      const form = store && store.form ? store.form : null;
      const background = form && form.global_background_image ? form.global_background_image : null;

      return !!(background && background.preview);
    },

    isVoucherUrlDefault() {
      const item = this.getItem();
      return !!(item && item.voucher_url_use_default);
    },

    voucherUrlInputValue() {
      if (this.isVoucherUrlDefault) {
        return this.buildDefaultVoucherUrl();
      }

      const item = this.getItem();
      return item && typeof item.voucher_url === 'string' ? item.voucher_url : '';
    }
  },

  methods: {
    buildDefaultVoucherUrl() {
      if (this.isOffersMode) {
        return `com.upmobil://multiben/offer/${this.product.product_id}`;
      }

      return `com.upmobil://multiben/benefits/voucher/${this.product.product_id}?is_selected=true`;
    },

    onVoucherUrlDefaultChange(checked) {
      this.updateProductItemField('voucher_url_use_default', checked);

      if (checked) {
        this.updateProductItemField('voucher_url', this.buildDefaultVoucherUrl());
      }
    },

    onVoucherUrlInput(event) {
      this.updateProductItemField('voucher_url', event.target.value);
    },

    getItem() {
      return this.product.items && this.product.items[0] ? this.product.items[0] : null;
    },

    updateProductItemField(field, value) {
      const item = this.getItem();

      if (!item) {
        return;
      }

      this.$store.dispatch(`${STORE_NAMESPACE}/updateProductItemField`, {
        productLocalId: this.product.local_id,
        itemLocalId: item.local_id,
        field,
        value
      });
    },

    updateProductField(field, value) {
      this.$store.dispatch(`${STORE_NAMESPACE}/updateProductField`, {
        localId: this.product.local_id,
        field,
        value
      });
    },

    buildToken(variableKey) {
      return `{{${variableKey}}}`;
    },

    normalizeColor(value) {
      return value && String(value).trim() ? value : '#ffffff';
    },

    isTransparentBackground(value) {
      return !value || !String(value).trim() || String(value).toLowerCase() === 'transparent';
    },

    getBadgeList(positionKey) {
      const item = this.getItem();

      if (!item || !item.badges) {
        return [];
      }

      return Array.isArray(item.badges[positionKey]) ? item.badges[positionKey] : [];
    },

    updateBadgeList(positionKey, list) {
      const item = this.getItem();

      if (!item) {
        return;
      }

      this.$store.dispatch(`${STORE_NAMESPACE}/updateProductItemNestedField`, {
        productLocalId: this.product.local_id,
        itemLocalId: item.local_id,
        parentField: 'badges',
        field: positionKey,
        value: list
      });
    },

    createBadgePayload() {
      return {
        local_id: `badge_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        content_mode: 'custom',
        product_variable_key: this.isOffersMode ? 'name' : 'product_name',
        title: {
          ro: '',
          en: ''
        },
        title_color: '#000000',
        image: '',
        image_file: null,
        image_preview: '',
        remove_image: false,
        background_color: '',
        corner_radius: 12,
        use_d_shape: false,
        paddings: {
          top: 10,
          left: 10,
          right: 10,
          bottom: 10
        },
        margins: {
          top: 0,
          left: 0,
          right: 0,
          bottom: 0
        }
      };
    },

    setBadgeTransparent(positionKey, badgeIndex, checked) {
      if (checked) {
        this.updateBadgeField(positionKey, badgeIndex, 'background_color', '');
        return;
      }

      this.updateBadgeField(positionKey, badgeIndex, 'background_color', '#ffffff');
    },

    addBadge(positionKey) {
      const currentList = this.getBadgeList(positionKey).slice();
      currentList.push(this.createBadgePayload());
      this.updateBadgeList(positionKey, currentList);
    },

    removeBadge(positionKey, badgeIndex) {
      const currentList = this.getBadgeList(positionKey).slice();
      currentList.splice(badgeIndex, 1);
      this.updateBadgeList(positionKey, currentList);
    },

    updateBadgeField(positionKey, badgeIndex, field, value) {
      const currentList = this.getBadgeList(positionKey).slice();

      if (!currentList[badgeIndex]) {
        return;
      }

      currentList[badgeIndex] = {
        ...currentList[badgeIndex],
        [field]: value
      };

      this.updateBadgeList(positionKey, currentList);
    },

    updateBadgeI18n(positionKey, badgeIndex, field, lang, value) {
      const currentList = this.getBadgeList(positionKey).slice();

      if (!currentList[badgeIndex]) {
        return;
      }

      const currentBadge = currentList[badgeIndex];
      const currentI18n = currentBadge[field] && typeof currentBadge[field] === 'object'
          ? currentBadge[field]
          : { ro: '', en: '' };

      currentList[badgeIndex] = {
        ...currentBadge,
        [field]: {
          ...currentI18n,
          [lang]: value
        }
      };

      this.updateBadgeList(positionKey, currentList);
    },

    appendToken(positionKey, badgeIndex, variableKey) {
      const currentList = this.getBadgeList(positionKey).slice();

      if (!currentList[badgeIndex]) {
        return;
      }

      const token = `{{${variableKey}}}`;
      const currentBadge = currentList[badgeIndex];
      const currentTitle = currentBadge.title || { ro: '', en: '' };

      currentList[badgeIndex] = {
        ...currentBadge,
        title: {
          ro: `${currentTitle.ro || ''}${token}`,
          en: `${currentTitle.en || ''}${token}`
        }
      };

      this.updateBadgeList(positionKey, currentList);
    },

    updateBadgeNumber(positionKey, badgeIndex, field, value) {
      const parsedValue = Number(value);

      this.updateBadgeField(
          positionKey,
          badgeIndex,
          field,
          Number.isNaN(parsedValue) ? 0 : parsedValue
      );
    },

    updateBadgePadding(positionKey, badgeIndex, field, value) {
      const currentList = this.getBadgeList(positionKey).slice();

      if (!currentList[badgeIndex]) {
        return;
      }

      const parsedValue = Number(value);
      const currentBadge = currentList[badgeIndex];
      const currentPaddings = currentBadge.paddings && typeof currentBadge.paddings === 'object'
          ? currentBadge.paddings
          : { top: 0, left: 0, right: 0, bottom: 0 };

      currentList[badgeIndex] = {
        ...currentBadge,
        paddings: {
          ...currentPaddings,
          [field]: Number.isNaN(parsedValue) ? 0 : parsedValue
        }
      };

      this.updateBadgeList(positionKey, currentList);
    },

    updateBadgeMargin(positionKey, badgeIndex, field, value) {
      const currentList = this.getBadgeList(positionKey).slice();

      if (!currentList[badgeIndex]) {
        return;
      }

      const parsedValue = Number(value);
      const currentBadge = currentList[badgeIndex];
      const currentMargins = currentBadge.margins && typeof currentBadge.margins === 'object'
          ? currentBadge.margins
          : { top: 0, left: 0, right: 0, bottom: 0 };

      currentList[badgeIndex] = {
        ...currentBadge,
        margins: {
          ...currentMargins,
          [field]: Number.isNaN(parsedValue) ? 0 : parsedValue
        }
      };

      this.updateBadgeList(positionKey, currentList);
    },

    onBadgeImageChange(positionKey, badgeIndex, event) {
      const file = event.target.files && event.target.files[0];

      if (!file) {
        return;
      }

      if (!file.type || !file.type.startsWith('image/')) {
        return;
      }

      if (file.size > MAX_IMAGE_SIZE_BYTES) {
        return;
      }

      const reader = new FileReader();

      reader.onload = () => {
        const currentList = this.getBadgeList(positionKey).slice();

        if (!currentList[badgeIndex]) {
          return;
        }

        currentList[badgeIndex] = {
          ...currentList[badgeIndex],
          image_file: file,
          image_preview: reader.result,
          image: reader.result
        };

        this.updateBadgeList(positionKey, currentList);
      };

      reader.readAsDataURL(file);
    },

    removeBadgeImage(positionKey, badgeIndex) {
      const currentList = this.getBadgeList(positionKey).slice();

      if (!currentList[badgeIndex]) {
        return;
      }

      currentList[badgeIndex] = {
        ...currentList[badgeIndex],
        image_file: null,
        image_preview: '',
        image: ''
      };

      this.updateBadgeList(positionKey, currentList);
    },

    onProductImageChange(event) {
      const file = event.target.files && event.target.files[0];

      if (!file) {
        return;
      }

      if (!file.type || !file.type.startsWith('image/')) {
        this.$store.dispatch(`${STORE_NAMESPACE}/setProductImage`, {
          localId: this.product.local_id,
          image: {
            file: null,
            preview: '',
            error: this.$t('superUpLang.widget.carousel.productEditor.errors.invalidImage')
          }
        });
        this.resetProductImageInput();
        return;
      }

      if (file.size > MAX_IMAGE_SIZE_BYTES) {
        this.$store.dispatch(`${STORE_NAMESPACE}/setProductImage`, {
          localId: this.product.local_id,
          image: {
            file: null,
            preview: '',
            error: this.$t('superUpLang.widget.carousel.productEditor.errors.imageTooLarge')
          }
        });
        this.resetProductImageInput();
        return;
      }

      const reader = new FileReader();

      reader.onload = () => {
        this.$store.dispatch(`${STORE_NAMESPACE}/setProductImage`, {
          localId: this.product.local_id,
          image: {
            file,
            preview: reader.result,
            error: ''
          }
        });
      };

      reader.readAsDataURL(file);
    },

    removeProductImage() {
      this.$store.dispatch(`${STORE_NAMESPACE}/clearProductImage`, this.product.local_id);
      this.resetProductImageInput();
    },

    resetProductImageInput() {
      const refName = `productImageInput_${this.product.local_id}`;
      const inputRef = this.$refs[refName];

      if (inputRef) {
        inputRef.value = '';
      }
    }
  }
};
</script>

<style scoped>
.compact-form-card__header {
  padding: 10px 14px;
  font-size: 14px;
}

.compact-form-card__body {
  padding: 14px;
}

.compact-form-row {
  margin-left: -6px;
  margin-right: -6px;
}

.compact-form-row > [class*='col-'] {
  padding-left: 6px;
  padding-right: 6px;
}

.compact-form-group {
  margin-bottom: 10px;
}

.compact-label {
  display: inline-block;
  margin-bottom: 5px;
  font-size: 13px;
  font-weight: 600;
  color: #343a40;
}

.compact-input {
  height: 34px;
  padding: 6px 10px;
  font-size: 13px;
}

.compact-help-text {
  margin-top: 4px;
  font-size: 11px;
  line-height: 1.35;
  color: #6c757d;
}

.compact-check-group {
  margin-bottom: 8px;
  min-height: auto;
}

.compact-check-label {
  font-size: 13px;
  line-height: 1.3;
  padding-top: 1px;
}

.compact-file-input {
  font-size: 13px;
}

.widget-carousel-product-editor__section {
  margin-bottom: 18px;
}

.widget-carousel-product-editor__section-title {
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 8px;
}

.widget-carousel-product-editor__color-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.widget-carousel-product-editor__color-input {
  width: 42px;
  min-width: 42px;
  height: 34px;
  padding: 2px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  background: #fff;
}

.widget-carousel-product-editor__error {
  margin-top: 4px;
  font-size: 12px;
  color: #dc3545;
}

.widget-carousel-product-editor__image-preview-wrap {
  margin-top: 10px;
  max-width: 260px;
}

.widget-carousel-product-editor__image-preview {
  display: block;
  width: 100%;
  border: 1px solid #dee2e6;
  border-radius: 6px;
}

.widget-carousel-product-editor__badge-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}

.widget-carousel-product-editor__badge-slot {
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 12px;
  background: #fff;
}

.widget-carousel-product-editor__badge-slot-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.widget-carousel-product-editor__badge-empty {
  font-size: 12px;
  color: #6c757d;
  background: #f8f9fa;
  border: 1px dashed #ced4da;
  border-radius: 6px;
  padding: 10px;
}

.widget-carousel-product-editor__badge-item {
  border-top: 1px solid #eef1f4;
  padding-top: 12px;
  margin-top: 12px;
}

.widget-carousel-product-editor__badge-item:first-of-type {
  border-top: 0;
  padding-top: 0;
  margin-top: 0;
}

.widget-carousel-product-editor__badge-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  font-size: 13px;
  color: #343a40;
}

.widget-carousel-product-editor__inactive-box {
  margin-top: 8px;
}

.widget-carousel-product-editor__inactive-reason {
  margin-top: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #dc3545;
  word-break: break-word;
}

.widget-carousel-product-editor__locked-note {
  margin-top: 12px;
  padding: 10px 12px;
  border: 1px solid #f5c6cb;
  border-radius: 6px;
  background: #fff5f5;
  color: #b02a37;
  font-size: 12px;
  font-weight: 600;
}

.widget-carousel-product-editor__token-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

@media (max-width: 767.98px) {
  .widget-carousel-product-editor__badge-slot-header,
  .widget-carousel-product-editor__badge-item-header {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>